import type { TextureSelections } from '@/model/schema';
import type { Rng } from './rng';

/**
 * Static registry of texture-set IDs per SLOT. Each slot maps to one
 * on-disk category directory via `TEXTURE_SLOT_DIR`; several slots can
 * share a directory (e.g. `outdoorFloor` and `pavement` both live
 * under `public/textures/outdoorFloor/` but serve different roles).
 *
 * Generation is sync (and we want the URL hash to round-trip across
 * reloads regardless of fetch timing), so we pin the catalogue here
 * rather than at runtime.
 *
 * Keep this in sync with `public/textures/manifest.json`. New texture
 * sets need an entry in BOTH files plus a `meta.json` in the texture
 * directory.
 */
export const TEXTURE_REGISTRY: Record<keyof TextureSelections, string[]> = {
  exteriorWall: ['painted_plaster', 'wall_cladding', 'brick_wall'],
  interiorWall: [],
  interiorFloor: ['laminate_floor'],
  outdoorFloor: ['sparse_grass'],
  pavement: ['clean_asphalt'],
  roof: [],
  fence: [],
};

/**
 * Which on-disk category directory (`public/textures/<dir>/...`) each
 * schema slot resolves to. Kept in a dedicated map so multiple slots
 * can share a directory without duplicating files.
 */
export const TEXTURE_SLOT_DIR: Record<keyof TextureSelections, string> = {
  exteriorWall: 'exteriorWall',
  interiorWall: 'interiorWall',
  interiorFloor: 'interiorFloor',
  outdoorFloor: 'outdoorFloor',
  pavement: 'outdoorFloor',
  roof: 'roof',
  fence: 'fence',
};

/**
 * Probability of randomly assigning a texture for a given slot, given
 * that at least one option exists. `outdoorFloor`, `pavement`, and
 * `interiorFloor` are pinned at 1.0 so every house consistently wears
 * grass on the lawn, asphalt on the hardscape, and laminate inside —
 * otherwise the site would flicker between textured / bare variants
 * every re-roll. `exteriorWall` stays probabilistic so flat-colour
 * houses still appear in the mix.
 */
const TEXTURE_PROBABILITY: Record<keyof TextureSelections, number> = {
  exteriorWall: 0.75,
  interiorWall: 0.0,
  interiorFloor: 1.0,
  outdoorFloor: 1.0,
  pavement: 1.0,
  roof: 0.0,
  fence: 0.0,
};

/**
 * Pick texture-set ids per slot using the registry above. Returns
 * `undefined` when no slot has a registered option, in which case
 * the caller should omit the field from the resulting `HouseConfig`.
 */
export function pickTextures(rng: Rng): TextureSelections | undefined {
  const out: TextureSelections = {};
  let any = false;
  for (const [slot, options] of Object.entries(TEXTURE_REGISTRY) as Array<
    [keyof TextureSelections, string[]]
  >) {
    if (options.length === 0) continue;
    const prob = TEXTURE_PROBABILITY[slot] ?? 0;
    if (!rng.chance(prob)) continue;
    const pick = options[rng.int(0, options.length - 1)]!;
    out[slot] = pick;
    any = true;
  }
  return any ? out : undefined;
}
